import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Users, Trash2, UserPlus, Building2, Shield, Home, Phone, Pencil, Check, X, Loader2, Mail, AlertTriangle } from 'lucide-react';

function roleColor(role) {
  const map = { management: 'bg-blue-600', guard: 'bg-amber-600' };
  return map[role] || 'bg-emerald-600';
}

function RoleIcon({ role }) {
  if (role === 'management') return <Building2 className="w-4 h-4 text-white" />;
  if (role === 'guard') return <Shield className="w-4 h-4 text-white" />;
  return <Home className="w-4 h-4 text-white" />;
}

function roleBadgeLabel(member) {
  const typeMap = { resident_owner: 'Owner', non_resident_owner: 'Non-Res', tenant: 'Tenant' };
  if (member.resident_type && typeMap[member.resident_type]) return typeMap[member.resident_type];
  const roleMap = { management: 'Mgmt', guard: 'Guard' };
  return roleMap[member.role] || 'Resident';
}

function PhoneEditor({ member, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(member.phone || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await onSave(member.id, trimmed);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5 mt-0.5" data-testid={`user-phone-row-${member.id}`}>
        <Phone className="w-3 h-3 text-slate-400 shrink-0" />
        <span className="text-xs text-slate-500 font-mono truncate" data-testid={`user-phone-value-${member.id}`}>
          {member.phone || <em className="text-slate-400 not-italic">no phone</em>}
        </span>
        <button
          onClick={() => { setValue(member.phone || ''); setEditing(true); }}
          className="text-[10px] font-medium text-gk-teal hover:underline ml-0.5"
          data-testid={`user-phone-edit-${member.id}`}
        >
          edit
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 mt-0.5" data-testid={`user-phone-edit-row-${member.id}`}>
      <Phone className="w-3 h-3 text-slate-400 shrink-0" />
      <Input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setEditing(false);
        }}
        placeholder="+91-9876543210"
        className="h-7 px-2 text-xs font-mono w-44"
        data-testid={`user-phone-input-${member.id}`}
      />
      <button
        onClick={save}
        disabled={saving || !value.trim()}
        className="text-emerald-600 hover:text-emerald-800 disabled:opacity-50 shrink-0"
        title="Save"
        data-testid={`user-phone-save-${member.id}`}
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
      </button>
      <button
        onClick={() => { setValue(member.phone || ''); setEditing(false); }}
        className="text-slate-400 hover:text-slate-600 shrink-0"
        title="Cancel"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function EmailEditor({ member, onSave }) {
  // Email changes are sensitive (email == login identity). We use a confirm
  // modal instead of inline editing so management has to deliberately commit.
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const openModal = () => {
    setValue(member.email || '');
    setOpen(true);
  };

  const save = async () => {
    const trimmed = value.trim().toLowerCase();
    const original = (member.email || '').toLowerCase();
    if (!trimmed || trimmed === original) {
      setOpen(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      return; // visual hint via disabled button below
    }
    setSaving(true);
    try {
      await onSave(member.id, trimmed);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const trimmed = value.trim().toLowerCase();
  const original = (member.email || '').toLowerCase();
  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  const isUnchanged = trimmed === original;

  return (
    <>
      <button
        onClick={openModal}
        className="text-[10px] font-medium text-gk-teal hover:underline ml-1 inline-flex items-center gap-0.5"
        data-testid={`user-email-edit-${member.id}`}
        title="Edit login email"
      >
        <Pencil className="w-2.5 h-2.5" />
        edit
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`user-email-edit-modal-${member.id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Change Login Email
            </DialogTitle>
            <DialogDescription>
              Update the login email for <strong>{member.name}</strong>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800 leading-snug">
                Email is the user's <strong>login identity</strong> — future OTPs will be sent to the
                new address. Both the old and new email will receive a confirmation alert, and the
                change is recorded in the audit log.
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Current email</Label>
              <p className="text-sm font-mono bg-slate-50 px-3 py-2 rounded border border-slate-200 truncate">
                {member.email || <em className="text-slate-400">none</em>}
              </p>
            </div>

            <div className="space-y-1">
              <Label htmlFor={`new-email-${member.id}`} className="text-xs text-slate-500">New email</Label>
              <Input
                id={`new-email-${member.id}`}
                type="email"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="resident@example.com"
                autoFocus
                className="font-mono text-sm"
                data-testid={`user-email-input-${member.id}`}
              />
              {value.trim() && !isValid && (
                <p className="text-[11px] text-red-600">Enter a valid email address.</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
              data-testid={`user-email-cancel-${member.id}`}
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !isValid || isUnchanged}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid={`user-email-save-${member.id}`}
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <>Update email</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function UsersTab({
  communityStats,
  communityUsers,
  handleDeleteUser,
  handleEditUserPhone,
  handleEditUserEmail,
  showCreateUserModal,
  setShowCreateUserModal,
  userForm,
  setUserForm,
  handleCreateUser
}) {
  return (
    <>
      <Card className="shadow-lg border-0">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Users className="w-5 h-5 shrink-0" />
              Community Users
            </CardTitle>
            <Button
              data-testid="create-user-button"
              onClick={() => setShowCreateUserModal(true)}
              className="bg-blue-600 hover:bg-blue-700"
              size="sm"
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Add User
            </Button>
          </div>
          {communityStats && (
            <Badge className="bg-blue-600 w-fit">
              {communityStats.community?.name || 'My Community'}
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          {communityStats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-700">{communityStats.users?.management || 0}</p>
                <p className="text-xs text-blue-600">Management</p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-700">{communityStats.users?.residents || 0}</p>
                <p className="text-xs text-emerald-600">Residents</p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-700">{communityStats.users?.guards || 0}</p>
                <p className="text-xs text-amber-600">Guards</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-gk-navy">{communityStats.users?.guards_remaining || 0}</p>
                <p className="text-xs text-gk-navy-light">Guard Slots</p>
              </div>
            </div>
          )}

          {communityUsers.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No users in this community</p>
          ) : (
            <div className="space-y-3">
              {communityUsers.map((member) => (
                <div
                  key={member.id}
                  data-testid={`community-user-${member.id}`}
                  className="p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`p-2 rounded-full shrink-0 ${roleColor(member.role)}`}>
                        <RoleIcon role={member.role} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-sm truncate">{member.name}</p>
                        <div className="flex items-center gap-1 min-w-0">
                          <p className="text-xs text-slate-500 truncate" data-testid={`user-email-value-${member.id}`}>
                            {member.email}
                          </p>
                          {member.role !== 'management' && (
                            <EmailEditor member={member} onSave={handleEditUserEmail} />
                          )}
                        </div>
                        {member.villa_number && (
                          <p className="text-xs text-slate-400">Villa: {member.villa_number}</p>
                        )}
                        <PhoneEditor member={member} onSave={handleEditUserPhone} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge className={`text-[10px] whitespace-nowrap ${roleColor(member.role)}`}>
                        {roleBadgeLabel(member)}
                      </Badge>
                      {member.role !== 'management' && (
                        <Button
                          data-testid={`delete-user-${member.id}`}
                          onClick={() => handleDeleteUser(member.id, member.name, member.role)}
                          size="sm"
                          variant="ghost"
                          className="text-red-500 hover:bg-red-50 h-7 w-7 p-0"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Add Community User
            </DialogTitle>
            <DialogDescription>Create a new resident account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="user-name">Full Name *</Label>
              <Input
                id="user-name"
                value={userForm.name}
                onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                placeholder="Enter full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-email">Email *</Label>
              <Input
                id="user-email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-phone">Phone Number *</Label>
              <Input
                id="user-phone"
                value={userForm.phone}
                onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                placeholder="+91-XXXXXXXXXX"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-role">Role *</Label>
              <select
                data-testid="user-role-trigger"
                value={userForm.role}
                onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                className="flex h-9 w-full items-center rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="">Select role</option>
                <option value="resident_owner">Resident Owner</option>
                <option value="non_resident_owner">Non-Resident Owner</option>
                <option value="tenant">Tenant</option>
                <option value="guard">Guard</option>
              </select>
            </div>
            {['resident_owner', 'non_resident_owner', 'tenant'].includes(userForm.role) && (
              <div className="space-y-2">
                <Label htmlFor="user-villa">Villa Number *</Label>
                <Input
                  id="user-villa"
                  data-testid="user-villa-input"
                  value={userForm.villa_number}
                  onChange={(e) => setUserForm({...userForm, villa_number: e.target.value})}
                  placeholder="e.g., A-101"
                />
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleCreateUser}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Create User
              </Button>
              <Button onClick={() => setShowCreateUserModal(false)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
